/**
 * Annotation Task Management API
 *
 * Provides:
 * - Batch management (CRUD, activate, archive)
 * - Task management (list, assign, skip, submit)
 * - Data export from dora_sessions
 * - Statistics and reporting
 * - Golden test case export
 */

import apiClient from './client';
import type {
  AnnotationBatch,
  AnnotationTask,
  AnnotationLabel,
  BatchCreate,
  BatchUpdate,
  BatchListResponse,
  BatchListParams,
  TaskCreate,
  TaskListResponse,
  TaskListParams,
  TaskAssignRequest,
  LabelCreate,
  ExportPreviewRequest,
  ExportPreviewResponse,
  ExportExecuteRequest,
  ExportExecuteResponse,
  StatsResponse,
  GoldenExportRequest,
  GoldenExportResponse,
} from '@/types/annotation';

// ============================================================================
// Batch API
// ============================================================================

export const annotationBatchApi = {
  /**
   * List all annotation batches
   */
  list: async (params?: BatchListParams): Promise<BatchListResponse> => {
    const response = await apiClient.get('/annotations/batches', { params });
    return response.data;
  },

  /**
   * Get a single batch by ID or batch_id code
   */
  get: async (batchId: string): Promise<AnnotationBatch> => {
    const response = await apiClient.get(`/annotations/batches/${batchId}`);
    return response.data;
  },

  /**
   * Create a new annotation batch
   */
  create: async (data: BatchCreate): Promise<AnnotationBatch> => {
    const response = await apiClient.post('/annotations/batches', data);
    return response.data;
  },

  /**
   * Update a batch
   */
  update: async (batchId: string, data: BatchUpdate): Promise<AnnotationBatch> => {
    const response = await apiClient.put(`/annotations/batches/${batchId}`, data);
    return response.data;
  },

  /**
   * Activate a draft batch
   */
  activate: async (batchId: string): Promise<AnnotationBatch> => {
    const response = await apiClient.post(`/annotations/batches/${batchId}/activate`);
    return response.data;
  },

  /**
   * Archive a batch
   */
  archive: async (batchId: string): Promise<AnnotationBatch> => {
    const response = await apiClient.post(`/annotations/batches/${batchId}/archive`);
    return response.data;
  },

  /**
   * Restore an archived batch
   */
  restore: async (batchId: string): Promise<AnnotationBatch> => {
    const response = await apiClient.post(`/annotations/batches/${batchId}/restore`);
    return response.data;
  },

  /**
   * Delete a batch
   */
  delete: async (batchId: string): Promise<void> => {
    await apiClient.delete(`/annotations/batches/${batchId}`);
  },
};

// ============================================================================
// Task API
// ============================================================================

export const annotationTaskApi = {
  /**
   * List annotation tasks
   */
  list: async (params?: TaskListParams): Promise<TaskListResponse> => {
    const response = await apiClient.get('/annotations/tasks', { params });
    return response.data;
  },

  /**
   * Get a single task by ID or task_id code
   */
  get: async (taskId: string): Promise<AnnotationTask> => {
    const response = await apiClient.get(`/annotations/tasks/${taskId}`);
    return response.data;
  },

  /**
   * Get tasks assigned to current user
   */
  getMyQueue: async (limit?: number): Promise<TaskListResponse> => {
    const response = await apiClient.get('/annotations/tasks/my-queue', {
      params: { limit },
    });
    return response.data;
  },

  /**
   * Get next available task (auto-assigns to current user)
   */
  getNext: async (batchId?: string): Promise<AnnotationTask | null> => {
    const response = await apiClient.get('/annotations/tasks/next', {
      params: { batch_id: batchId },
    });
    return response.data;
  },

  /**
   * Create a task manually
   */
  create: async (data: TaskCreate): Promise<AnnotationTask> => {
    const response = await apiClient.post('/annotations/tasks', data);
    return response.data;
  },

  /**
   * Assign a task to a user
   */
  assign: async (taskId: string, data?: TaskAssignRequest): Promise<AnnotationTask> => {
    const response = await apiClient.post(`/annotations/tasks/${taskId}/assign`, data || {});
    return response.data;
  },

  /**
   * Skip a task
   */
  skip: async (taskId: string): Promise<AnnotationTask> => {
    const response = await apiClient.post(`/annotations/tasks/${taskId}/skip`);
    return response.data;
  },

  /**
   * Submit a label for a task
   */
  submit: async (taskId: string, data: LabelCreate): Promise<AnnotationLabel> => {
    const response = await apiClient.post(`/annotations/tasks/${taskId}/submit`, data);
    return response.data;
  },
};

// ============================================================================
// Export API
// ============================================================================

export const annotationExportApi = {
  /**
   * Preview messages to export from dora_sessions
   */
  preview: async (data: ExportPreviewRequest): Promise<ExportPreviewResponse> => {
    const response = await apiClient.post('/annotations/export/preview', data);
    return response.data;
  },

  /**
   * Execute data export and create batch with tasks
   */
  execute: async (data: ExportExecuteRequest): Promise<ExportExecuteResponse> => {
    const response = await apiClient.post('/annotations/export/execute', data);
    return response.data;
  },
};

// ============================================================================
// Stats API
// ============================================================================

export const annotationStatsApi = {
  /**
   * Get comprehensive annotation statistics
   */
  getAll: async (): Promise<StatsResponse> => {
    const response = await apiClient.get('/annotations/stats');
    return response.data;
  },

  /**
   * Get statistics for a specific batch
   */
  getBatch: async (batchId: string): Promise<Record<string, unknown>> => {
    const response = await apiClient.get(`/annotations/stats/batches/${batchId}`);
    return response.data;
  },

  /**
   * Export golden test cases
   */
  exportGolden: async (data: GoldenExportRequest): Promise<GoldenExportResponse> => {
    const response = await apiClient.post('/annotations/export-golden', data);
    return response.data;
  },
};

// ============================================================================
// Combined API Export
// ============================================================================

export const annotationsApi = {
  batches: annotationBatchApi,
  tasks: annotationTaskApi,
  export: annotationExportApi,
  stats: annotationStatsApi,
};
